import { useCallback, useSyncExternalStore } from 'react';
import { STORAGE_PREFIX } from '../constants';

// ---------------------------------------------------------------------------
// Tracks which tours a user has already seen, per UID in localStorage.
// ---------------------------------------------------------------------------

let listeners: Array<() => void> = [];
let version = 0;
function subscribe(cb: () => void) {
  listeners = [...listeners, cb];
  return () => { listeners = listeners.filter((l) => l !== cb); };
}
function getSnapshot() { return version; }
function emitChange() { version++; listeners.forEach((l) => l()); }

function seenKey(uid: string) {
  return `${STORAGE_PREFIX}:${uid}:seenTours`;
}

function readSeen(uid: string): string[] {
  try {
    const raw = window.localStorage.getItem(seenKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSeen(uid: string, tours: string[]) {
  try {
    window.localStorage.setItem(seenKey(uid), JSON.stringify(tours));
  } catch {
    // silently ignore
  }
}

export function useSeenTours(uid: string | undefined) {
  const storeVersion = useSyncExternalStore(subscribe, getSnapshot);

  const seen = uid ? readSeen(uid) : [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  void storeVersion; // trigger re-render on change

  const hasSeen = useCallback(
    (tourId: string) => (uid ? readSeen(uid).includes(tourId) : true),
    [uid],
  );

  const markSeen = useCallback(
    (tourId: string) => {
      if (!uid) return;
      const current = readSeen(uid);
      if (!current.includes(tourId)) {
        writeSeen(uid, [...current, tourId]);
        emitChange();
      }
    },
    [uid],
  );

  const resetTour = useCallback(
    (tourId: string) => {
      if (!uid) return;
      writeSeen(uid, readSeen(uid).filter((t) => t !== tourId));
      emitChange();
    },
    [uid],
  );

  return { seen, hasSeen, markSeen, resetTour };
}
